import camelCase from 'lodash/camelCase'
import clone from 'lodash/clone'
import get from 'lodash/get'
import isEqual from 'lodash/isEqual'
import isObjectLike from 'lodash/isObjectLike'
import isPlainObject from 'lodash/isPlainObject'
import mapKeys from 'lodash/mapKeys'
import mapValues from 'lodash/mapValues'
import merge from 'lodash/merge'
import pick from 'lodash/pick'
import setWith from 'lodash/setWith'
import snakeCase from 'lodash/snakeCase'

// Inlined from map-keys-deep-lodash (v1.2.3) — https://github.com/odynvolk/map-keys-deep-lodash
function mapKeysDeep(obj, cb, isRecursive) {
  if (!obj && !isRecursive) return {}
  if (!isRecursive && (typeof obj === 'string' || typeof obj === 'number' || typeof obj === 'boolean')) return {}
  if (Array.isArray(obj)) return obj.map(item => mapKeysDeep(item, cb, true))
  if (!isPlainObject(obj)) return obj
  return mapValues(mapKeys(obj, cb), value => mapKeysDeep(value, cb, true))
}

/**
 * Useful for getting a referentially stable default value for a prop or param that won't trigger
 * useEffects or other hook dependencies.
 *
 * @example
 * function MyComponent({ someProp = frozenObj }) {
 *   useEffect(() => {
 *     // Presuming someProp is not provided and the default is used,
 *     // this effect will not run on every render because someProp is stable
 *   }, [someProp])
 * }
 *
 * @see frozenArr
 */
export const frozenObj = Object.freeze({})

const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i
export const snakeCaseKeysDeep = obj => mapKeysDeep(obj, (val, key) => (uuidPattern.test(key) ? key : snakeCase(key)))
export const camelCaseKeysDeep = obj => mapKeysDeep(obj, (val, key) => (uuidPattern.test(key) ? key : camelCase(key)))

export const objectHasKeys = (obj, keys) => {
  if (!obj) return false

  // eslint-disable-next-line no-restricted-syntax
  for (const key of keys) {
    if (!Object.keys(obj).some(k => k === key)) {
      return false
    }
  }
  return true
}

export const isOptionShape = obj => objectHasKeys(obj, ['label', 'value']) || objectHasKeys(obj, ['label', 'options'])
export const toOptionShape = (value, { multiple, missingLabelSetToValue = true } = {}) => {
  if (value == null) return value
  const transformIfNeeded = val => (isOptionShape(val) ? val : { label: missingLabelSetToValue ? val : '', value: val })
  if (multiple && Array.isArray(value)) {
    return value.map(transformIfNeeded)
  }
  return transformIfNeeded(value)
}

export const flattenObject = obj => {
  const flattened = {}

  Object.keys(obj).forEach(key => {
    if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
      Object.assign(flattened, flattenObject(obj[key]))
    } else {
      flattened[key] = obj[key]
    }
  })

  return flattened
}

export const flattenObjectDeepPath = (obj, keyPrefix = '', delimiter = '.') => {
  // flattens object, preserving path
  // example:
  // {
  //   foo: 5,
  //   bar: { baz : 20 }
  // }
  // becomes:
  // {
  //   foo: 5,
  //   'bar.baz': 20
  // }
  const flattened = {}
  Object.keys(obj).forEach(key => {
    const keyPath = keyPrefix ? keyPrefix + delimiter + key : key
    if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
      Object.assign(flattened, flattenObjectDeepPath(obj[key], keyPath, delimiter))
    } else {
      flattened[keyPath] = obj[key]
    }
  })

  return flattened
}

/**
 * Just like pick but if there are no valid keys to pick it returns the whole object unchanged
 */
export function pickOrAll(obj, keys) {
  return keys && keys.length > 0 ? pick(obj, keys) : obj
}

/**
 * @setNestedValue
 * @param {*} obj => Object that we want to Update
 * @param {*} path => Path of the object that we want to update -> example: ['prop1', 'prop2', 'prop3'] or 'prop1.prop2.prop3'
 * @param {*} value => The value that we want to set to the object property
 *
 * Note: I created this function since "set" of lodash overrides the values of nested (deep) object properties
 *
 * const obj = {
 *  "planned_expense_amount": {
 *     "ui:widget": "currency",
 *     "ui:options": {
 *     "maxAmount": 1000000000.0
 *   }
 * }
 *
 * if i want to update the 'ui:widget' property using set => set(obj, ['planned_expense_amount', 'ui:widget'], { 'ui:widget': 'hidden' })
 * the object will we affected removing all the other properties and it'll return => {"planned_expense_amount": { 'ui:widget': 'hidden' }}
 *
 * So i decided to set the property cloning the obj and then merging with the original one
 *
 */
export const setNestedValue = (obj, path, value) => merge(obj, setWith(clone(obj), path, value, clone))

/**
 * Deep diff between two object-likes
 * @param  {Object} fromObject the original object
 * @param  {Object} toObject   the updated object
 * @return {Object}            a new object which represents the diff
 */
export function deepDiff(fromObject = {}, toObject = {}) {
  const changes = {}

  const buildPath = (path, obj, key) => (path === undefined ? key : `${path}.${key}`)

  const walk = (fromObject, toObject, path) => {
    for (const key of Object.keys(fromObject)) {
      const currentPath = buildPath(path, fromObject, key)
      // eslint-disable-next-line no-prototype-builtins
      if (!toObject.hasOwnProperty(key)) {
        changes[currentPath] = { from: get(fromObject, key) }
      }
    }

    for (const [key, to] of Object.entries(toObject)) {
      const currentPath = buildPath(path, toObject, key)
      // eslint-disable-next-line no-prototype-builtins
      if (!fromObject.hasOwnProperty(key)) {
        changes[currentPath] = { to }
      } else {
        const from = get(fromObject, key)
        if (!isEqual(from, to)) {
          if (isObjectLike(to) && isObjectLike(from)) {
            walk(from, to, currentPath)
          } else {
            changes[currentPath] = { from, to }
          }
        }
      }
    }
  }

  walk(fromObject ?? {}, toObject ?? {})

  return changes
}

export function ifDefinedElse(maybeDefined, fallbackValue) {
  if (maybeDefined === undefined) return fallbackValue
  return maybeDefined
}
